import { Button, Card, Divider, Popconfirm, Space, Table } from 'antd';
import { useLanguage } from '../utils/LanguageContext';
import translations from '../components/ComponentsTranslations.lang.json';
import {
  IncomingTransferRequestForm,
  OutgoingTransferRequestForm,
  RecurringDepositEditor,
  RecurringDepositForm,
} from './TransferForm';
import { useAuth } from '../utils/AuthContext';
import { TransferDirection, TransferStatus } from '../shared/constants/transferConstants';
import { formatAsDollars } from './common/util';
import { FormButton } from './common/ValidatedForm';
import { useBooleanState, useRefreshingAsync } from './common/commonHooks';

const ManageRecurringDeposit = ({ investmentAccountId, addingRecurring }) => {
  const { api, userEmployments } = useAuth();
  const { lang } = useLanguage(translations);

  const recurringDeposits = useRefreshingAsync(() => api.getUserRecurringDeposits(investmentAccountId));

  return (
    <Card title={lang('recurringDepositsTitle')}>
      {recurringDeposits &&
        (recurringDeposits.length === 0 ? (
          addingRecurring.value ? (
            <>
              <RecurringDepositForm investmentAccountId={investmentAccountId} onFinish={addingRecurring.setFalse} />
              <Button onClick={addingRecurring.setFalse}>{lang('cancel')}</Button>
            </>
          ) : (
            <>
              {userEmployments && userEmployments.length > 0 ? (
                <p>{lang('setupRecurringMessageHaveEmployment')}</p>
              ) : (
                <p>{lang('setupRecurringMessageNoEmployment')}</p>
              )}
              <p>{lang('noRecurringDepositSetup')}</p>
              <FormButton onClick={addingRecurring.setTrue}>{lang('addRecurringDeposit')}</FormButton>
            </>
          )
        ) : (
          <>
            {recurringDeposits.map((recurringDeposit, i) => (
              <RecurringDepositEditor
                key={i}
                investmentAccountId={investmentAccountId}
                recurringDeposit={recurringDeposit}
              />
            ))}
          </>
        ))}
    </Card>
  );
};

const TransferHistoryTable = ({ investmentAccountId }) => {
  const { lang, langForEnum } = useLanguage(translations);
  const { api } = useAuth();
  const transferHistory = useRefreshingAsync(async () => await api.getUserTransferHistory(investmentAccountId));

  if (!transferHistory) {
    return null;
  }

  const largeNumberToAlwaysSortFirst = 1e20;
  const sortValue = (t) => (t.createDate ? new Date(t.createDate).getTime() : largeNumberToAlwaysSortFirst);
  const transferHistorySorted = transferHistory.toSorted((a, b) => sortValue(b) - sortValue(a));

  const renderTranferStatus = ({ transferRequestId, transferStatus, cancelRequested }) => (
    <>
      {langForEnum(TransferStatus, transferStatus)}
      {transferStatus === TransferStatus.PENDING && (
        <>
          <Divider type="vertical" />
          {cancelRequested ? (
            lang('cancelRequested')
          ) : (
            <Popconfirm
              description={lang('transferRequestCancelConfirmation')}
              onConfirm={async () => await api.cancelTransferRequest(investmentAccountId, { transferRequestId })}
              okText={lang('yes')}
              cancelText={lang('no')}
            >
              <Button type="link" role="link">
                {lang('cancel')}
              </Button>
            </Popconfirm>
          )}
        </>
      )}
    </>
  );

  const columns = [
    { title: lang('amount'), render: (t) => formatAsDollars(t.amount), key: 'amount' },
    { title: lang('transferDirection'), render: (t) => langForEnum(TransferDirection, t.direction), key: 'direction' },
    { title: lang('transferDate'), render: (t) => t.createDate ?? lang('transferDatePending'), key: 'createDate' },
    { title: lang('status'), render: renderTranferStatus, key: 'status' },
  ];

  return (
    <Card title={lang('yourAccountTransfers')}>
      {transferHistory.length > 0 ? (
        <Table columns={columns} dataSource={transferHistorySorted} rowKey="transferRequestId" />
      ) : (
        <p>{lang('noTransfersMessage')}</p>
      )}
    </Card>
  );
};

const ManageTransfersForInvestmentAccount = ({ investmentAccountId }) => {
  const addingIncoming = useBooleanState(false);
  const addingOutgoing = useBooleanState(false);
  const addingRecurring = useBooleanState(false);

  const { lang } = useLanguage(translations);

  return (
    investmentAccountId && (
      <Space direction="vertical">
        {!addingIncoming.value && !addingOutgoing.value && (
          <ManageRecurringDeposit investmentAccountId={investmentAccountId} addingRecurring={addingRecurring} />
        )}
        {addingIncoming.value ? (
          <Card title={lang('newIncomingTransfer')}>
            <IncomingTransferRequestForm investmentAccountId={investmentAccountId} onFinish={addingIncoming.setFalse} />
            <Button onClick={addingIncoming.setFalse}>{lang('cancel')}</Button>
          </Card>
        ) : addingOutgoing.value ? (
          <Card title={lang('newOutgoingTransfer')}>
            <OutgoingTransferRequestForm investmentAccountId={investmentAccountId} onFinish={addingOutgoing.setFalse} />
            <Button onClick={addingOutgoing.setFalse}>{lang('cancel')}</Button>
          </Card>
        ) : !addingRecurring.value ? (
          <>
            <Space direction="horizontal">
              <FormButton onClick={addingIncoming.setTrue}>{lang('newIncomingTransfer')}</FormButton>
              <FormButton onClick={addingOutgoing.setTrue}>{lang('newOutgoingTransfer')}</FormButton>
            </Space>
            <TransferHistoryTable investmentAccountId={investmentAccountId} />
          </>
        ) : null}
      </Space>
    )
  );
};

const ManageTransfers = () => {
  const { api } = useAuth();

  const { investmentAccountId } = useRefreshingAsync(api.getInvestmentAccounts)?.[0] ?? {};

  return investmentAccountId && <ManageTransfersForInvestmentAccount investmentAccountId={investmentAccountId} />;
};

export default ManageTransfers;
